/**
 * Drawing a format's picture on a canvas from the program monitor's own media elements (1.7.0 stream B, Side by side,
 * and the Blur fit background): one decode for every panel, so nothing drifts. The same composition as the monitor's
 * DOM stage (frameAt in ProgramViewer.tsx): V1 with its transition and fade to black, Blur fit's blurred background,
 * then the video layers with their placement, motion keys, crop and opacity. Colour corrections are not drawn on the
 * canvases (the DOM stage and the export have them).
 */
import {
  BLUR_FIT_SIGMA, TICKS_PER_SECOND, cropAt, fadeFactor, layersAt, pictureFades, resolveAt, storylinePictureFades, valueAt,
  type LayerClip, type SequenceDocument, type Size, type VideoClip,
} from '@prism/contracts';
import { canvasDraw, coverSource } from './monitor-geometry';

const S = TICKS_PER_SECOND;

export type PictureElement = HTMLVideoElement | HTMLImageElement;

/** A media element's picture size (0×0 until it is known). */
export function mediaSize(el: Element | null | undefined): Size | null {
  if (el instanceof HTMLVideoElement) return el.videoWidth > 0 && el.videoHeight > 0 ? { width: el.videoWidth, height: el.videoHeight } : null;
  if (el instanceof HTMLImageElement) return el.naturalWidth > 0 && el.naturalHeight > 0 ? { width: el.naturalWidth, height: el.naturalHeight } : null;
  return null;
}

/** Whether an element has a picture to draw now. */
const drawable = (el: PictureElement) => (el instanceof HTMLVideoElement ? el.readyState >= 2 : el.complete && el.naturalWidth > 0);

/**
 * Whether an element shows the frame asked for (1.7.1): a video that is not seeking and has its current frame, an image
 * that has loaded. A video that failed (`error`) never will, so it does not hold the frame back.
 */
export const frameSettled = (el: PictureElement): boolean => (el instanceof HTMLVideoElement ? Boolean(el.error) || (!el.seeking && el.readyState >= 2) : el.complete);

/**
 * Whether every element the frame at `ph` needs is settled (1.7.1): the V1 clip, its transition partner and the layer
 * clips at the playhead. While paused, a frame is only painted when it is, so a scrub never paints a black or partial
 * frame between seeks (the panel repaints on the elements' seeked and loadeddata events).
 */
export function frameReady(doc: SequenceDocument, ph: number, elementFor: (clipId: string) => PictureElement | undefined): boolean {
  const hit = resolveAt(doc, ph);
  const ids = [hit?.placed.clip.id, hit?.outgoing?.placed.clip.id, ...layersAt(doc, ph).map((l) => l.clip.id)];
  for (const id of ids) {
    const el = id ? elementFor(id) : undefined;
    if (el && !frameSettled(el)) return false;
  }
  return true;
}

/** A layer's position, size and opacity at a source time: its motion keys (resolved for the format) over its static values. */
export function layerPlaceAt(c: LayerClip, sourceTicks: number): { x: number; y: number; scale: number; opacity: number } {
  const keys = c.motion?.keys;
  return {
    x: valueAt(keys, 'x', sourceTicks) ?? c.x,
    y: valueAt(keys, 'y', sourceTicks) ?? c.y,
    scale: valueAt(keys, 'scale', sourceTicks) ?? c.scale,
    opacity: valueAt(keys, 'opacity', sourceTicks) ?? c.opacity,
  };
}

let blurScratch: HTMLCanvasElement | null = null;
/** The Blur fit's background: the centre fill drawn small, then scaled up softly (and blurred where ctx.filter exists). */
export function drawBlurBackground(ctx: CanvasRenderingContext2D, el: PictureElement, size: Size, box: { x: number; y: number; width: number; height: number }) {
  if (typeof document === 'undefined') return;
  blurScratch ??= document.createElement('canvas');
  const sw = 32, sh = Math.max(1, Math.round((32 * box.height) / box.width));
  blurScratch.width = sw;
  blurScratch.height = sh;
  const sctx = blurScratch.getContext('2d');
  if (!sctx) return;
  const c = coverSource(size, box.width / box.height);
  sctx.drawImage(el, c.sx, c.sy, c.sw, c.sh, 0, 0, sw, sh);
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  // The export's sigma (BLUR_FIT_SIGMA of the short side), in this canvas's pixels.
  if ('filter' in ctx) ctx.filter = `blur(${Math.max(1, BLUR_FIT_SIGMA * Math.min(box.width, box.height)).toFixed(2)}px)`;
  // A little larger than the box, so the blur's soft edge stays outside it.
  const m = box.width * 0.06;
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.width, box.height);
  ctx.clip();
  ctx.drawImage(blurScratch, box.x - m, box.y - m, box.width + 2 * m, box.height + 2 * m);
  ctx.restore();
}

/**
 * Draw one format's frame at `ph` (sequence ticks) into a canvas of `width`×`height` pixels. `doc` is the format's
 * document (documentForFormat); `elementFor(clipId)` gives the monitor's element for a clip; `timeOf` the source seconds
 * an element shows now (during playback, its own time, so the crop and the frame shown agree).
 *
 * While paused it paints nothing until every element the frame needs is settled (frameReady), and returns false then:
 * the canvas keeps the last frame instead of flashing black while the elements seek (1.7.1). Returns true when painted.
 */
export function paintFormat(ctx: CanvasRenderingContext2D, doc: SequenceDocument, ph: number, width: number, height: number,
  elementFor: (clipId: string) => PictureElement | undefined, opts: { playing: boolean; vfades?: ReturnType<typeof storylinePictureFades> }): boolean {
  if (!opts.playing && !frameReady(doc, ph, elementFor)) return false;
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, width, height);
  const fa = width / height;
  const vfades = opts.vfades ?? storylinePictureFades(doc);
  const timeOf = (el: PictureElement, sourceTicks: number) => (opts.playing && el instanceof HTMLVideoElement ? el.currentTime * S : sourceTicks);
  const full = { x: 0, y: 0, width, height };
  const drawV1 = (clip: VideoClip, index: number, sourceTicks: number, local: number, alpha: number, dx: number, wipe: number | null) => {
    const el = elementFor(clip.id);
    if (!el || !drawable(el)) return;
    const size = mediaSize(el);
    if (!size) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    if (wipe !== null) { ctx.beginPath(); ctx.rect((1 - wipe) * width, 0, width, height); ctx.clip(); }
    ctx.translate(dx * width, 0);
    const at = timeOf(el, sourceTicks);
    if (clip.fit === 'blur') drawBlurBackground(ctx, el, size, full);
    const win = cropAt(size.width / size.height, fa, clip, at);
    const r = canvasDraw(win, size, full);
    ctx.drawImage(el, r.sx, r.sy, r.sw, r.sh, r.dx, r.dy, r.dw, r.dh);
    const fade = vfades[index] ? fadeFactor(vfades[index]!, local) : 1;
    if (fade < 1) { ctx.globalAlpha = alpha * (1 - fade); ctx.fillStyle = '#000'; ctx.fillRect(0, 0, width, height); }
    ctx.restore();
  };
  const hit = resolveAt(doc, ph);
  if (hit) {
    const out = hit.outgoing;
    const p = out?.progress ?? 1;
    const kind = hit.placed.transition?.kind;
    let inA = 1, outA = 1, inDx = 0, outDx = 0, wipe: number | null = null;
    if (out && kind === 'dissolve') inA = p;
    else if (out && kind === 'dip_to_black') { inA = p < 0.5 ? 0 : 2 * p - 1; outA = p < 0.5 ? 1 - 2 * p : 0; }
    else if (out && kind === 'wipe') wipe = p;
    else if (out && kind === 'slide') { inDx = 1 - p; outDx = -p; }
    if (out) drawV1(out.placed.clip, out.placed.index, out.sourceTicks, ph - out.placed.recordIn, outA, outDx, null);
    drawV1(hit.placed.clip, hit.placed.index, hit.sourceTicks, ph - hit.placed.recordIn, inA, inDx, out ? wipe : null);
  }
  for (const l of layersAt(doc, ph)) {
    const c = l.clip;
    const el = elementFor(c.id);
    if (!el || !drawable(el)) continue;
    const size = mediaSize(el);
    if (!size) continue;
    const at = timeOf(el, l.sourceTicks);
    const m = layerPlaceAt(c, l.sourceTicks);
    const f = fadeFactor(pictureFades(c, doc.frameRate), ph - c.recordInTicks);
    const bw = m.scale * width, bh = m.scale * height;
    const box = { x: (0.5 + m.x) * width - bw / 2, y: (0.5 + m.y) * height - bh / 2, width: bw, height: bh };
    const win = cropAt(size.width / size.height, fa, c, at);
    const r = canvasDraw(win, size, box);
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, m.opacity * f));
    ctx.beginPath();
    ctx.rect(box.x, box.y, box.width, box.height);
    ctx.clip();
    ctx.drawImage(el, r.sx, r.sy, r.sw, r.sh, r.dx, r.dy, r.dw, r.dh);
    ctx.restore();
  }
  ctx.restore();
  return true;
}
